import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Search, ArrowRight, Bus, Clock, MapPin, Users, RefreshCw, Loader2 } from "lucide-react";
import TourneesTabs from "@/components/TourneesTabs";

export default function GtfsTrips() {
  const [trips, setTrips] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [routeFilter, setRouteFilter] = useState("");
  const [directionFilter, setDirectionFilter] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (routeFilter) params.append("route_id", routeFilter);
      if (directionFilter !== "") params.append("direction", directionFilter);
      if (search) params.append("q", search);
      const [{ data: t }, { data: r }] = await Promise.all([
        api.get(`/gtfs/trips?${params.toString()}`),
        api.get("/logibus/transit/routes"),
      ]);
      setTrips(t);
      setRoutes(r);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [routeFilter, directionFilter, search]);

  const filtered = useMemo(() => {
    if (assignmentFilter === "assigned") return trips.filter((t) => t.assigned);
    if (assignmentFilter === "unassigned") return trips.filter((t) => !t.assigned);
    return trips;
  }, [trips, assignmentFilter]);

  const importGtfs = async (remote) => {
    setImporting(true);
    try {
      const { data } = await api.post(`/logibus/admin/import-gtfs?remote=${remote ? "true" : "false"}`);
      toast.success(`Import : ${data.trips} trips · ${data.stops} arrêts · ${data.shapes} pts shape (${data.source})`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setImporting(false); }
  };

  return (
    <div className="p-4 lg:p-6" data-testid="gtfs-trips-page">
      <TourneesTabs />
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Tournées GTFS · Bus publics</div>
          <h1 className="font-display font-black uppercase text-3xl lg:text-4xl tracking-tighter">Tournées</h1>
          <p className="text-sm text-zinc-600 mt-1">{trips.length} tournées importées depuis GTFS Suisse · Morges & Rolle.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => importGtfs(false)} disabled={importing} data-testid="reimport-local"
            className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            {importing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Réimporter local
          </button>
          <button onClick={() => importGtfs(true)} disabled={importing} data-testid="reimport-remote"
            className="inline-flex items-center gap-2 bg-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#ffd43a]">
            {importing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sync GTFS officiel
          </button>
        </div>
      </header>

      <div className="grid lg:grid-cols-4 gap-3 mb-4">
        <div className="border border-zinc-900 bg-white flex items-center gap-2 px-2 py-1.5">
          <Search size={14} className="text-zinc-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ligne / destination"
            data-testid="trip-search" className="flex-1 outline-none text-sm" />
        </div>
        <select value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)} data-testid="route-filter"
          className="border border-zinc-900 bg-white px-2 py-1.5 text-sm font-mono">
          <option value="">Toutes les lignes</option>
          {routes.map((r) => <option key={r.route_id} value={r.route_id}>Ligne {r.route_short_name} · {r.route_long_name}</option>)}
        </select>
        <select value={directionFilter} onChange={(e) => setDirectionFilter(e.target.value)} data-testid="direction-filter"
          className="border border-zinc-900 bg-white px-2 py-1.5 text-sm font-mono">
          <option value="">Tous les sens</option>
          <option value="0">Sens 0 (aller)</option>
          <option value="1">Sens 1 (retour)</option>
        </select>
        <div className="border border-zinc-900 grid grid-cols-3 text-[10px] font-bold uppercase tracking-widest">
          {[["all", "Toutes"], ["assigned", "Assignées"], ["unassigned", "À assigner"]].map(([k, l]) => (
            <button key={k} onClick={() => setAssignmentFilter(k)} data-testid={`assignment-${k}`}
              className={`py-1.5 border-r border-zinc-900 last:border-r-0 ${assignmentFilter === k ? "bg-[#FFC60A]" : "bg-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-zinc-900 overflow-hidden">
        <table className="w-full text-sm" data-testid="gtfs-trips-table">
          <thead className="bg-zinc-900 text-white">
            <tr>
              {["Ligne", "Direction", "Horaire", "Arrêts", "Véhicule", "Chauffeur", ""].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-mono tracking-widest uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-zinc-500 font-mono text-xs">Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-zinc-500">Aucune tournée GTFS — importez les données.</td></tr>
            )}
            {filtered.map((t) => (
              <tr key={t.trip_id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`trip-row-${t.trip_id}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 flex items-center justify-center font-display font-black text-xs border border-zinc-900"
                      style={{ background: `#${t.color || "FFC60A"}`, color: t.color === "0A0A0B" ? "#FFC60A" : "#0A0A0B" }}>
                      {t.route_short_name}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-bold tracking-tight truncate max-w-[200px]">{t.route_long_name}</div>
                      <div className="text-[10px] font-mono text-zinc-500">{t.trip_id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="text-sm font-semibold flex items-center gap-1"><ArrowRight size={11} /> {t.trip_headsign}</div>
                  <div className="text-[10px] font-mono text-zinc-500">sens {t.direction_id}</div>
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  <div className="flex items-center gap-1"><Clock size={11} className="text-zinc-500" /> {t.departure_time?.slice(0,5)} → {t.arrival_time?.slice(0,5)}</div>
                </td>
                <td className="px-3 py-2"><MapPin size={11} className="inline text-zinc-500 mr-1" />{t.stops_count}</td>
                <td className="px-3 py-2 text-xs">{t.vehicle ? <span className="inline-flex items-center gap-1"><Bus size={11} /> {t.vehicle.plate}</span> : <span className="text-zinc-400">—</span>}</td>
                <td className="px-3 py-2 text-xs">{t.driver ? <span className="inline-flex items-center gap-1"><Users size={11} /> {t.driver.name}</span> : <span className="text-zinc-400">—</span>}</td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/admin/gtfs-trips/${t.trip_id}`} data-testid={`view-trip-${t.trip_id}`}
                    className="inline-flex items-center gap-1 bg-zinc-900 text-[#FFC60A] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800">
                    Détail <ArrowRight size={11} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
